import { useEffect, useRef, useState } from "react";
import { Modal } from "./Modal";
import { Btn, Select } from "./ui";

// ── THE ORATOR BOX, NOT THE BROWSER'S (2026-10-31) ────────────────────────────────────────────
//
// Caleb, on the sign archive reason: "This is a repeated issue in our system it's how groups get
// named for example and we want to standardize getting rid of that and replacing it with a proper
// box built within the orator system and this would be an opportunity to build a standard rule and
// also go back and apply that system wide."
//
// window.prompt / confirm / alert draw the BROWSER's grey box: "campaign.oratorcrm.com says", a
// one-line text field, OK and Cancel. It cannot be styled, cannot offer a list to pick from, cannot
// say what the button will do, and it freezes the page while it is up. Fifteen of them had
// accumulated in the dashboard, each one written because it was the shortest thing to type.
//
// So there is ONE box, in two shapes, both awaited the way the browser's were so a call site reads
// the same:
//
//   if (!(await confirmBox({ title: "Delete \"X\"?", message: "…", confirmLabel: "Delete", danger: true }))) return;
//   const got = await askBox({ title: "New group", fields: [{ key: "name", label: "Group name" }] });
//   if (!got) return;            // cancelled
//   got.name                     // trimmed
//
// <DialogHost/> is mounted once, in main.tsx. If it is not mounted the box FAILS CLOSED -- a confirm
// answers no and an ask is cancelled -- rather than falling back to the browser's own box, which is
// the thing this exists to remove. .github/workflows/browser-popup-guard.py keeps them out.

export type AskField =
  | { kind?: "text"; key: string; label: string; initial?: string; placeholder?: string;
      required?: boolean; multiline?: boolean; max?: number; type?: "text" | "date" }
  // A SHORT LIST TO PICK ONE FROM, every option in view (0415: which election the campaign is in).
  // Each option may carry a line under it. A dropdown hides the choices; this is for when seeing
  // them all is the point.
  | { kind: "choice"; key: string; label: string; options: [string, string, string?][]; initial?: string;
      required?: boolean }
  | { kind: "select"; key: string; label: string; options: [string, string][]; initial?: string;
      required?: boolean; placeholder?: string };

type Common = { title: string; message?: string; warn?: string; confirmLabel?: string; danger?: boolean };
type Req =
  | (Common & { kind: "confirm"; resolve: (v: boolean) => void })
  | (Common & { kind: "ask"; fields: AskField[]; resolve: (v: Record<string, string> | null) => void });

let enqueue: ((r: Req) => void) | null = null;

/** Ask a yes/no question in an Orator box. Resolves true only on the confirm button. */
export function confirmBox(opts: Common): Promise<boolean> {
  return new Promise((resolve) => {
    if (enqueue) enqueue({ ...opts, kind: "confirm", resolve });
    else { console.error("confirmBox: <DialogHost/> is not mounted"); resolve(false); }
  });
}

/** Ask for one or more values in an Orator box. Resolves the trimmed values by key, or null if
 *  cancelled. A required field keeps the confirm button greyed until it is filled. */
export function askBox(opts: Common & { fields: AskField[] }): Promise<Record<string, string> | null> {
  return new Promise((resolve) => {
    if (enqueue) enqueue({ ...opts, kind: "ask", resolve });
    else { console.error("askBox: <DialogHost/> is not mounted"); resolve(null); }
  });
}

export function DialogHost() {
  const [queue, setQueue] = useState<Req[]>([]);
  useEffect(() => {
    enqueue = (r) => setQueue((p) => [...p, r]);
    return () => { enqueue = null; };
  }, []);
  const cur = queue[0];
  if (!cur) return null;
  const done = () => setQueue((p) => p.slice(1));
  // A NEW KEY PER REQUEST so a second box opened straight after the first starts from its own
  // initial values, not the last one's typing.
  return cur.kind === "confirm"
    ? <ConfirmCard key={queue.length + cur.title} req={cur} done={done} />
    : <AskCard key={queue.length + cur.title} req={cur} done={done} />;
}

function Head({ req }: { req: Common }) {
  return (
    <div className="px-4 py-3 border-b border-border">
      <div className="font-extrabold">{req.title}</div>
      {req.message && <div className="text-sm text-muted mt-1 whitespace-pre-line">{req.message}</div>}
    </div>
  );
}

function Warn({ text }: { text?: string }) {
  if (!text) return null;
  return <div className="rounded-lg border border-warn bg-warn/10 px-3 py-2 text-sm whitespace-pre-line">{text}</div>;
}

function Foot({ req, ok, onCancel, onOk }: { req: Common; ok: boolean; onCancel: () => void; onOk: () => void }) {
  return (
    <div className="px-4 py-3 border-t border-border flex justify-end gap-2">
      <Btn variant="ghost" type="button" onClick={onCancel}>Cancel</Btn>
      <Btn type="submit" disabled={!ok} onClick={onOk}
        className={req.danger ? "!bg-bad" : ""}>{req.confirmLabel || "OK"}</Btn>
    </div>
  );
}

function ConfirmCard({ req, done }: { req: Extract<Req, { kind: "confirm" }>; done: () => void }) {
  const answer = (v: boolean) => { req.resolve(v); done(); };
  return (
    <Modal onClose={() => answer(false)}>
      <Head req={req} />
      {req.warn && <div className="p-4"><Warn text={req.warn} /></div>}
      <Foot req={req} ok onCancel={() => answer(false)} onOk={() => answer(true)} />
    </Modal>
  );
}

function AskCard({ req, done }: { req: Extract<Req, { kind: "ask" }>; done: () => void }) {
  const [vals, setVals] = useState<Record<string, string>>(
    () => Object.fromEntries(req.fields.map((f) => [f.key, f.initial ?? ""])));
  const first = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const ok = req.fields.every((f) => !f.required || (vals[f.key] || "").trim());
  const answer = (v: Record<string, string> | null) => { req.resolve(v); done(); };
  const submit = () => {
    if (!ok) return;
    answer(Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, v.trim()])));
  };
  const set = (k: string, v: string) => setVals((p) => ({ ...p, [k]: v }));
  let focused = false;
  return (
    <Modal onClose={() => answer(null)}>
      {/* A FORM, so pressing Enter in a one-line field does what typing a name and pressing Enter
          has always done in the browser's box. That is the form's own behaviour, not a shortcut. */}
      <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <Head req={req} />
        <div className="p-4 flex flex-col gap-3">
          <Warn text={req.warn} />
          {req.fields.map((f) => {
            const id = `ask-${f.key}`;
            if (f.kind === "select") {
              return (
                <div key={f.key}>
                  <span className="block text-sm font-bold mb-1.5">{f.label}</span>
                  <Select value={vals[f.key] || ""} onChange={(v) => set(f.key, v)} width="full"
                    options={f.placeholder ? [["", f.placeholder], ...f.options] : f.options} />
                </div>
              );
            }
            if (f.kind === "choice") {
              return (
                <div key={f.key} role="radiogroup" aria-label={f.label}>
                  <span className="block text-sm font-bold mb-1.5">{f.label}</span>
                  <div className="flex flex-col gap-1">
                    {f.options.map(([v, l, sub]) => (
                      <label key={v} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 cursor-pointer ${vals[f.key] === v ? "border-accent bg-accent/10" : "border-border"}`}>
                        <input type="radio" name={id} className="mt-1" checked={vals[f.key] === v} onChange={() => set(f.key, v)} />
                        <span>
                          <span className="block text-sm font-semibold">{l}</span>
                          {sub && <span className="block text-xs text-muted">{sub}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            }
            const grab = !focused; focused = true;
            const max = f.max ?? 300;
            return (
              <label key={f.key} htmlFor={id} className="block">
                <span className="block text-sm font-bold mb-1.5">{f.label}</span>
                {f.multiline ? (
                  <textarea id={id} className="ctl resize-y min-h-[72px]" value={vals[f.key] || ""} placeholder={f.placeholder}
                    ref={grab ? (el) => { first.current = el; } : undefined}
                    onChange={(e) => set(f.key, e.target.value.slice(0, max))} />
                ) : (
                  <input id={id} className="ctl" type={f.type ?? "text"} value={vals[f.key] || ""} placeholder={f.placeholder}
                    ref={grab ? (el) => { first.current = el; } : undefined}
                    onChange={(e) => set(f.key, e.target.value.slice(0, max))} />
                )}
              </label>
            );
          })}
        </div>
        <Foot req={req} ok={ok} onCancel={() => answer(null)} onOk={() => { /* the form's submit handles it */ }} />
      </form>
    </Modal>
  );
}
